import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { getFacilityTypes } from "@services/facilitytype";
import { createUserPreferredFacility } from "@services/payment";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { getCurrentUser, getCurrentUserId } from "@utils/userUtils";
import {
  findDuplicateNameAndAddress,
  getCuratedAddFacilityByTypeOptions,
  resolveAddFacilityByTypeFromCatalog,
  resolveStateAbbrevFromList,
  type NameAddressFacility,
  type ResolvedAddFacilityByType,
} from "@utils/addFacilityByType";

const Panel = styled(Box)({
  width: "100%",
  marginTop: "12px",
  padding: "16px",
  boxSizing: "border-box",
  border: "1px solid #99d4e1",
  borderRadius: "8px",
  backgroundColor: "#f3fdff",
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

/** External label + control — same pattern as FacilityValidationScreen search fields. */
const FieldBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  minWidth: 0,
});

const FieldLabel = styled("label")({
  display: "block",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 500,
  fontSize: "14px",
  color: "#065b70",
  marginBottom: "8px",
  lineHeight: 1.5,
});

const outlinedControl = {
  backgroundColor: "#fdffff",
  minHeight: 40,
  "& fieldset": {
    border: "1px solid #99d4e1",
    borderRadius: "8px",
  },
  "&:hover fieldset": {
    border: "1px solid #0a829d",
  },
  "&.Mui-focused fieldset": {
    border: "1px solid #0a829d",
  },
} as const;

const Field = styled(TextField)({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...outlinedControl,
    "& input": {
      padding: "10px 12px",
      height: "20px",
      boxSizing: "content-box",
      fontFamily: "Rethink Sans, sans-serif",
      fontWeight: 600,
      fontSize: "14px",
      color: "#262626",
    },
  },
});

const OutlinedSelect = styled(Select<string>)({
  width: "100%",
  ...outlinedControl,
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  "& .MuiSelect-select": {
    padding: "10px 12px",
    minHeight: "20px",
    boxSizing: "content-box",
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #99d4e1",
    borderRadius: "8px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #0a829d",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    border: "1px solid #0a829d",
  },
});

const FormGrid = styled(Box)({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: "16px",
  alignItems: "start",
});

const HelperText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "12px",
  color: "#4f6f78",
  lineHeight: 1.45,
  marginTop: "8px",
  marginLeft: "2px",
});

function RequiredMark(): JSX.Element {
  return <span style={{ color: "#D13438" }}> *</span>;
}

const PrimaryButton = styled(Button)({
  alignSelf: "flex-start",
  textTransform: "none",
  fontFamily: "Rethink Sans, sans-serif",
  fontWeight: 600,
  fontSize: "14px",
  background: "linear-gradient(to right, #0a829d, #58c2d8)",
  color: "#e6fafc",
  borderRadius: "8px",
  padding: "10px 20px",
  "&:hover": {
    background: "linear-gradient(to right, #065b70, #0a829d)",
  },
});

export interface AddFacilityByTypePanelProps {
  searchName: string;
  searchCity: string;
  searchState: string;
  searchPhone: string;
  searchEmail: string;
  states: Array<{
    id?: number;
    name?: string;
    state_province?: string;
    state_abbreviation?: string;
  }>;
  existingFacilities?: NameAddressFacility[];
  cartFacilities?: NameAddressFacility[];
  resetKey?: string;
}

type Step = "cta" | "type" | "identity";

export default function AddFacilityByTypePanel({
  searchName,
  searchCity,
  searchState,
  searchPhone,
  searchEmail,
  states,
  existingFacilities = [],
  cartFacilities = [],
  resetKey,
}: AddFacilityByTypePanelProps): JSX.Element {
  const navigate = useNavigate();
  const typeOptions = useMemo(() => getCuratedAddFacilityByTypeOptions(), []);
  const [step, setStep] = useState<Step>("cta");
  const [selectedSlug, setSelectedSlug] = useState("");
  const [resolvedType, setResolvedType] = useState<ResolvedAddFacilityByType | null>(
    null,
  );
  const [legalName, setLegalName] = useState(searchName);
  const [street, setStreet] = useState("");
  const [city, setCity] = useState(searchCity);
  const [stateAbbrev, setStateAbbrev] = useState(
    resolveStateAbbrevFromList(searchState, states),
  );
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState(searchPhone);
  const [email, setEmail] = useState(searchEmail);
  const [licenseNumber, setLicenseNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setStep("cta");
    setSelectedSlug("");
    setResolvedType(null);
    setLegalName(searchName);
    setStreet("");
    setCity(searchCity);
    setStateAbbrev(resolveStateAbbrevFromList(searchState, states));
    setZip("");
    setPhone(searchPhone);
    setEmail(searchEmail);
    setLicenseNumber("");
  }, [resetKey]);

  const handleOpenTypes = () => {
    setStep("type");
  };

  const handleTypeChange = async (event: SelectChangeEvent<string>) => {
    const slug = event.target.value;
    setSelectedSlug(slug);
    if (!slug) {
      setResolvedType(null);
      return;
    }
    let catalog: { id: number; type_slug: string; display_name: string }[] = [];
    try {
      const res = await getFacilityTypes();
      catalog = res.facility_types || [];
    } catch {
      catalog = [];
    }
    const resolved = resolveAddFacilityByTypeFromCatalog(slug, catalog);
    setResolvedType(resolved);
    if (resolved) {
      setLegalName((prev) => prev || searchName);
      setCity((prev) => prev || searchCity);
      setStateAbbrev((prev) => prev || resolveStateAbbrevFromList(searchState, states));
      setPhone((prev) => prev || searchPhone);
      setEmail((prev) => prev || searchEmail);
      setStep("identity");
    }
  };

  const handleSubmit = async () => {
    const name = legalName.trim();
    const street1 = street.trim();
    const cityVal = city.trim();
    const stateVal = resolveStateAbbrevFromList(stateAbbrev, states);
    const zipVal = zip.trim();
    if (!resolvedType) {
      toast.error("Select a facility type.");
      return;
    }
    if (!name) {
      toast.error("Enter the licensed or legal facility name.");
      return;
    }
    if (!street1) {
      toast.error("Enter the street address.");
      return;
    }
    if (!cityVal) {
      toast.error("Enter the city.");
      return;
    }
    if (!stateVal) {
      toast.error("Select a state.");
      return;
    }
    if (!zipVal) {
      toast.error("Enter the ZIP code.");
      return;
    }

    const candidate: NameAddressFacility = {
      facility_name: name,
      address_1: street1,
      city: cityVal,
      state: stateVal,
      postal_code: zipVal,
    };
    const duplicate = findDuplicateNameAndAddress(candidate, [
      ...existingFacilities,
      ...cartFacilities,
    ]);
    if (duplicate) {
      toast.error(
        "A facility with this name and address is already on your account or in the cart.",
      );
      return;
    }

    const userId = Number(getCurrentUserId() || "0");
    if (!userId) {
      toast.error("Please sign in to add a facility.");
      return;
    }
    const user = getCurrentUser();
    const username =
      user?.first_name && user?.last_name
        ? `${user.first_name} ${user.last_name}`
        : user?.display_name || user?.first_name || "";
    const canSalesBypass = user?.can_sales_bypass === true;
    const license = licenseNumber.trim();

    setSubmitting(true);
    try {
      const facility_details = {
        user_id: String(userId),
        facility_name: name,
        facility_type: resolvedType.display_name,
        facility_type_id: resolvedType.facility_type_id,
        facility_type_slug: resolvedType.slug,
        primary_profile_slug: resolvedType.slug,
        city: cityVal,
        state: stateVal,
        country: getSelectedCountryCode() || "USA",
        address_1: street1,
        postal_code: zipVal,
        phone_number: phone.trim() || undefined,
        skip_npi_validation: true,
        verification_source: "self_reported",
        ...(license ? { state_license_number: license } : {}),
        username: username || undefined,
        pricing_tier_id: 0,
        bypass_checkout: canSalesBypass,
        discount_coupon: "",
      };

      const response = await createUserPreferredFacility({
        user_id: userId,
        facility_details,
        is_verified: canSalesBypass,
      });

      const preferredFacilityId =
        response.data?.id ?? (response as { data?: { data?: { id?: number } } })?.data?.data?.id;

      try {
        sessionStorage.removeItem("filtered_selectedFacility");
        sessionStorage.removeItem("selected_plan");
        sessionStorage.setItem(
          "preferred_facility_for_pricing",
          JSON.stringify({
            id: preferredFacilityId,
            user_preferred_facility_id: preferredFacilityId,
            user_id: userId,
            is_verified: response.data?.is_verified === true,
            is_active: response.data?.is_active ?? true,
            facility_name: name,
            facility_type: resolvedType.display_name,
            facility_type_id: resolvedType.facility_type_id,
            facility_type_slug: resolvedType.slug,
            primary_profile_slug: resolvedType.slug,
            city: cityVal,
            state: stateVal,
            country: facility_details.country,
            address_1: street1,
            postal_code: zipVal,
            phone_number: facility_details.phone_number,
            skip_npi_validation: true,
            verification_source: "self_reported",
            state_license_number: license || undefined,
            discount_coupon_name: null,
            discount_end_date: null,
            facility_details,
          }),
        );
      } catch {
        // Pricing can still read route state.
      }

      toast.success(
        `${name} added as a self-reported ${resolvedType.display_name}. Continue to checkout.`,
      );
      navigate("/dashboard/facilities", {
        state: {
          currentView: "pricing",
          fromPreferredFacilityId: preferredFacilityId,
          facility_type_id: resolvedType.facility_type_id,
          pricingReloadToken: Date.now(),
        },
        replace: true,
      });
    } catch (e) {
      const message =
        e instanceof Error ? e.message : "Failed to add facility.";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Panel data-testid="add-facility-by-type-panel">
      <Typography
        sx={{
          fontFamily: "Rethink Sans, sans-serif",
          fontWeight: 600,
          fontSize: "15px",
          color: "#065b70",
        }}
      >
        No NPI on file? Add this facility by type.
      </Typography>
      <Typography
        sx={{
          fontFamily: "Rethink Sans, sans-serif",
          fontSize: "13px",
          color: "#4f6f78",
          lineHeight: 1.45,
        }}
      >
        Self-reported and unverified. Compliance uses the type you select.
        Licensed Adult Family Homes (for example Lakemont in Washington) should
        choose Adult Family Home, not Memory Care.
      </Typography>

      {step === "cta" ? (
        <PrimaryButton
          type="button"
          onClick={handleOpenTypes}
          aria-label="Add facility by type"
        >
          Add facility by type
        </PrimaryButton>
      ) : (
        <FieldBlock>
          <FieldLabel htmlFor="add-facility-by-type-select">
            Facility type
          </FieldLabel>
          <OutlinedSelect
            id="add-facility-by-type-select"
            displayEmpty
            value={selectedSlug}
            onChange={handleTypeChange}
            inputProps={{ "aria-label": "Facility type" }}
          >
            <MenuItem value="">
              <em>Select facility type…</em>
            </MenuItem>
            {typeOptions.map((opt) => (
              <MenuItem
                key={opt.slug}
                value={opt.slug}
                aria-label={opt.display_name}
              >
                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                    {opt.display_name}
                  </Typography>
                  <Typography sx={{ fontSize: 12, color: "#4f6f78", whiteSpace: "normal" }}>
                    {opt.description}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </OutlinedSelect>
        </FieldBlock>
      )}

      {step === "identity" && resolvedType ? (
        <>
          <Typography
            sx={{
              fontFamily: "Rethink Sans, sans-serif",
              fontSize: "13px",
              color: "#065b70",
              fontWeight: 600,
            }}
          >
            Identity for {resolvedType.display_name}
          </Typography>
          <FieldBlock data-field-block="legal-name">
            <FieldLabel htmlFor="add-by-type-legal-name">
              Licensed / legal name
              <RequiredMark />
            </FieldLabel>
            <Field
              id="add-by-type-legal-name"
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              inputProps={{ "aria-label": "Licensed or legal name" }}
            />
          </FieldBlock>
          <FieldBlock data-field-block="street">
            <FieldLabel htmlFor="add-by-type-street">
              Street address
              <RequiredMark />
            </FieldLabel>
            <Field
              id="add-by-type-street"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              inputProps={{ "aria-label": "Street address" }}
            />
          </FieldBlock>
          <FormGrid>
            <FieldBlock data-field-block="city">
              <FieldLabel htmlFor="add-by-type-city">
                City
                <RequiredMark />
              </FieldLabel>
              <Field
                id="add-by-type-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                inputProps={{ "aria-label": "City" }}
              />
            </FieldBlock>
            <FieldBlock data-field-block="state">
              <FieldLabel htmlFor="add-by-type-state">
                State
                <RequiredMark />
              </FieldLabel>
              <OutlinedSelect
                id="add-by-type-state"
                displayEmpty
                value={stateAbbrev}
                onChange={(e) => setStateAbbrev(e.target.value)}
                inputProps={{ "aria-label": "State" }}
              >
                <MenuItem value="">
                  <em>Select state…</em>
                </MenuItem>
                {states.map((s) => {
                  const abbr = (s.state_abbreviation || "").toUpperCase();
                  if (!abbr) return null;
                  return (
                    <MenuItem key={abbr} value={abbr}>
                      {abbr} ({s.name || s.state_province || abbr})
                    </MenuItem>
                  );
                })}
              </OutlinedSelect>
            </FieldBlock>
            <FieldBlock data-field-block="zip">
              <FieldLabel htmlFor="add-by-type-zip">
                ZIP
                <RequiredMark />
              </FieldLabel>
              <Field
                id="add-by-type-zip"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                inputProps={{ "aria-label": "ZIP" }}
              />
            </FieldBlock>
            <FieldBlock data-field-block="phone">
              <FieldLabel htmlFor="add-by-type-phone">Phone</FieldLabel>
              <Field
                id="add-by-type-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputProps={{ "aria-label": "Phone" }}
              />
            </FieldBlock>
          </FormGrid>
          <FieldBlock data-field-block="email">
            <FieldLabel htmlFor="add-by-type-email">Email</FieldLabel>
            <Field
              id="add-by-type-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputProps={{ "aria-label": "Email" }}
            />
          </FieldBlock>
          <FieldBlock data-field-block="license">
            <FieldLabel htmlFor="add-by-type-license">
              State license number
            </FieldLabel>
            <Field
              id="add-by-type-license"
              value={licenseNumber}
              onChange={(e) => setLicenseNumber(e.target.value)}
              inputProps={{ "aria-label": "State license number" }}
            />
            <HelperText>
              Recommended. Optional for now — enter the state license if you have
              it (e.g. WA DSHS 755861).
            </HelperText>
          </FieldBlock>
          <PrimaryButton
            type="button"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={submitting}
            aria-label="Continue to checkout"
          >
            {submitting ? "Adding…" : "Continue to checkout"}
          </PrimaryButton>
        </>
      ) : null}
    </Panel>
  );
}
